// Resolve the human-readable name for a Guacamole connection cache key
// (the values held in `active_perm` and `vm_cache`). Used by the sidebar's
// ActiveConnections list and by Copilot's panel header to show "which
// connection is this?" labels consistently.
//
// The cache key is either a numeric cloud_permission row id, or a
// `pool_<pool_id>_<access_id>` string for pooled sessions. Pool sessions
// have additional resolution rules — see inline comments below.

import { permission_list, vms_list, vms_list_original } from '../userData.js';


function getPermissionForCacheKey(key) {
  const permissions = permission_list.get();
  if (permissions[key]) return permissions[key];

  if (typeof key === 'string' && key.startsWith('pool')) {
    const [, pool_id, access_id] = key.split('_');

    // Prefer the explicit access id when available (pool_<poolId>_<accessId>).
    if (access_id && permissions[access_id]) return permissions[access_id];
    if (pool_id && permissions[pool_id]) return permissions[pool_id];

    // Some pooled sessions only provide pool_<poolId>. Fall back to the
    // matching vmpool permission by vm.id so labels still render.
    if (pool_id) {
      const pool_permission = Object.values(permissions).find(permission => {
        if (!permission) return false;
        const is_pool_permission_type = (permission.type === 'vmpool' || permission.type === 'vmpool-app');
        const has_vm = Boolean(permission.vm);
        const matches_pool_id = has_vm && String(permission.vm.id) === String(pool_id);
        return is_pool_permission_type && matches_pool_id;
      });
      if (pool_permission) return pool_permission;
    }
  }
  return null;
}


/**
 * @param {string|number} perm_id - The cache key (cloud_permission row id, or
 *   a `pool_<poolId>_<accessId>` string).
 * @returns {string} A user-facing label like "Desktop, MyVM" — never empty.
 */
export function getPermissionDisplayName(perm_id) {
  const permission = getPermissionForCacheKey(perm_id);

  const vm_from_original = vm_id =>
    vms_list_original.get().find(({ id }) => String(id) === String(vm_id));

  const get_vm_descriptor = vm_id => {
    if (!vm_id) return '';
    const vm = vms_list.get()[vm_id] || vm_from_original(vm_id);
    return (vm && (vm.descriptor || vm.description || vm.name)) || '';
  };

  if (!permission) {
    // Hash-embedded but not in permission list
    if (perm_id && typeof perm_id === 'string' && perm_id.startsWith('pool')) {
      const [, pool_id] = perm_id.split('_');
      const pool_name = get_vm_descriptor(pool_id);
      return pool_name ? `Desktop, ${pool_name}` : 'Desktop';
    }
    const vm_name = get_vm_descriptor(perm_id);
    return vm_name ? `Desktop, ${vm_name}` : 'Desktop';
  }

  const type = permission.type;
  const is_pool_type = (type === 'vmpool' || type === 'vmpool-app');
  const direct_vm_id = permission.vm && permission.vm.id;
  const pool_vm_id = permission.pool_vm && permission.pool_vm.id;
  const latest_pool_vm_id = permission.last_vmpool_vm_id;
  // Keep pool identity visible in the sidebar label; for non-pool perms,
  // prefer the most recently used pool VM if known.
  const vm_id = is_pool_type
    ? direct_vm_id
    : (latest_pool_vm_id || pool_vm_id || direct_vm_id);

  const vm_desc = get_vm_descriptor(vm_id);
  const perm_desc = (type === 'vm' || type === 'vmpool')
    ? 'Desktop'
    : ((permission.app && permission.app.display_name) || 'Desktop');

  return vm_desc ? (perm_desc + ', ' + vm_desc) : perm_desc;
}
